/**
 * Data transformation utilities to eliminate duplication across components
 */
import { getPrimaryImageUrl } from './imageUtils';
import { convertUTCToMeetingTimezone, formatDateWithTimeRelative, formatTimeInTimezone } from './dateTimeUtils';
import { formatProfileLocationLabel } from './locationUtils';

/**
 * Clean an ID by removing any appended indices or invalid characters
 * @param {string|number} id - The ID to clean
 * @returns {string|null} Cleaned ID or null if invalid
 */
export const cleanId = (id) => {
  if (!id) return null;
  const idStr = String(id);
  // Remove any colon and everything after it (e.g., "id:1" -> "id")
  // This handles cases where React keys or indices get accidentally appended
  const cleaned = idStr.split(':')[0].trim();
  return cleaned || null;
};

/**
 * Format price for display
 * @param {number} price - Numeric price value
 * @returns {string} Formatted price string with $ symbol
 */
export const formatPrice = (price) => {
  if (price === null || price === undefined || isNaN(price)) return 'Free';
  return `$${price.toFixed(2)}`;
};

/**
 * Get date range for a specific time period
 * @param {string} type - Type of date range: 'today', 'tomorrow', 'thisWeek', 'nextWeek'
 * @param {Date} baseDate - Base date to calculate from (defaults to now)
 * @returns {Object|null} - Object with start and end dates, or null if invalid type
 */
export const getDateRange = (type, baseDate = new Date()) => {
  const now = new Date(baseDate);
  
  switch (type) {
    case 'today':
      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date(now);
      todayEnd.setHours(23, 59, 59, 999);
      return { start: todayStart, end: todayEnd };
      
    case 'tomorrow':
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStart = new Date(tomorrow);
      tomorrowStart.setHours(0, 0, 0, 0);
      const tomorrowEnd = new Date(tomorrow);
      tomorrowEnd.setHours(23, 59, 59, 999);
      return { start: tomorrowStart, end: tomorrowEnd };
      
    case 'thisWeek':
      const thisWeekStart = new Date(now);
      thisWeekStart.setDate(thisWeekStart.getDate() - thisWeekStart.getDay());
      thisWeekStart.setHours(0, 0, 0, 0);
      const thisWeekEnd = new Date(thisWeekStart);
      thisWeekEnd.setDate(thisWeekEnd.getDate() + 6);
      thisWeekEnd.setHours(23, 59, 59, 999);
      return { start: thisWeekStart, end: thisWeekEnd };
      
    case 'nextWeek':
      const currentWeekStart = new Date(now);
      currentWeekStart.setDate(currentWeekStart.getDate() - currentWeekStart.getDay());
      const nextWeekStart = new Date(currentWeekStart);
      nextWeekStart.setDate(nextWeekStart.getDate() + 7);
      nextWeekStart.setHours(0, 0, 0, 0);
      const nextWeekEnd = new Date(nextWeekStart);
      nextWeekEnd.setDate(nextWeekEnd.getDate() + 6);
      nextWeekEnd.setHours(23, 59, 59, 999);
      return { start: nextWeekStart, end: nextWeekEnd };

    case 'next7Days':
      // Next 7 days: today through 6 days from now (7 days total), matching mobile
      const next7DaysStart = new Date(now);
      next7DaysStart.setHours(0, 0, 0, 0);
      const next7DaysEnd = new Date(now);
      next7DaysEnd.setDate(next7DaysEnd.getDate() + 6);
      next7DaysEnd.setHours(23, 59, 59, 999);
      return { start: next7DaysStart, end: next7DaysEnd };

    default:
      return null;
  }
};

/**
 * Check if a date is within a date range
 * @param {Date} date - Date to check
 * @param {Object} range - Object with start and end dates
 * @returns {boolean} - Whether the date is in range
 */
export const isDateInRange = (date, range) => {
  if (!range || !date) return false;
  return date >= range.start && date <= range.end;
};

/**
 * Check if a meeting date matches a time filter
 * @param {Date|string} meetingDate - The meeting's scheduled date
 * @param {string} timeFilter - The time filter (e.g., 'Today', 'Tomorrow', 'This Week', 'Next Week', 'All')
 * @param {Date} baseDate - Base date for calculations
 * @returns {boolean} - Whether the meeting matches the time filter
 */
export const matchesTimeFilter = (meetingDate, timeFilter, baseDate = new Date()) => {
  if (timeFilter === 'All') {
    // "All" means show all upcoming meetings (date > now)
    const now = new Date(baseDate);
    const date = meetingDate instanceof Date ? meetingDate : new Date(meetingDate);
    return date > now;
  }

  const date = meetingDate instanceof Date ? meetingDate : new Date(meetingDate);

  switch (timeFilter) {
    case 'Next 7 days':
    case 'Next 7 Days':
      return isDateInRange(date, getDateRange('next7Days', baseDate));
    case 'Today':
      return isDateInRange(date, getDateRange('today', baseDate));
    case 'Tomorrow':
      return isDateInRange(date, getDateRange('tomorrow', baseDate));
    case 'This Week':
      return isDateInRange(date, getDateRange('thisWeek', baseDate));
    case 'Next Week':
    case 'Next week':
      return isDateInRange(date, getDateRange('nextWeek', baseDate));
    default:
      return true;
  }
};

/**
 * Default image position (center of image)
 */
export const DEFAULT_IMAGE_POSITION = { x: 50, y: 50 };

/**
 * Convert image position coordinates to float values for API
 * @param {Object} imagePosition - Image position object with x and y properties
 * @returns {Object|null} Object with float x and y values, or null if invalid
 */
export const convertImagePositionToFloat = (imagePosition) => {
  if (!imagePosition || typeof imagePosition !== 'object') {
    return null;
  }
  
  const x = parseFloat(imagePosition.x);
  const y = parseFloat(imagePosition.y);
  
  if (isNaN(x) || isNaN(y)) {
    return null;
  }
  
  return { x, y };
};

/**
 * Transform a single meeting object
 * @param {Object} meeting - Raw meeting data from API
 * @returns {Object} Transformed meeting data
 */
export const transformMeeting = (meeting) => {
  if (!meeting) return null;
  
  
  // Calculate time until the meeting
  const timeUntil = getTimeUntil(meeting.scheduled_date);
  
  // Format the date and time for display (in meeting timezone)
  const date = formatDate(meeting.scheduled_date, { format: 'short', timezone: meeting.timezone || null });
  const time = formatTime(meeting.scheduled_date, meeting.timezone || null);
  
  // Try to find any ID field in the meeting object
  let meetingId = meeting.id || meeting._id;
  
  // Handle null/undefined IDs - these need to be fixed at the backend level
  if (!meetingId || meetingId === null || meetingId === 'null') {
    // Meeting missing ID - this should be fixed in the backend
    meetingId = null;
  }
  

  const transformedMeeting = {
    id: meetingId,
    name: meeting.name,
    title: meeting.name, // Alias for MeetingCard compatibility
    description: meeting.what_to_expect,
    location: meeting.address,
    address: meeting.address,
    city: meeting.city,
    scheduled_date: meeting.scheduled_date,
    difficulty: meeting.difficulty,
    event_type: meeting.event_type,
    current_participants: meeting.current_participants || 0,
    attendees: meeting.current_participants || 0, // Alias for MeetingCard compatibility
    status: meeting.status || 'upcoming',
    is_recurring: meeting.is_recurring,
    recurrence_pattern: meeting.recurrence_pattern,
    // Get primary image from images array, or null if no images
    image: getPrimaryImageUrl(meeting.images),
    images: meeting.images, // Also include full images array
    imagePosition: meeting.image_position, // Map from backend snake_case to frontend camelCase
    // Additional fields for MeetingCard compatibility
    timeUntil: timeUntil,
    date: date,
    time: time,
    created_at: meeting.created_at,
    updated_at: meeting.updated_at,
    club_id: meeting.club_id,
    created_by: meeting.created_by,
    distance_from_user: meeting.distance_from_user
  };


  return transformedMeeting;
};

/**
 * Transform an array of meetings
 * @param {Array} meetings - Array of raw meeting data
 * @returns {Array} Array of transformed meeting data
 */
export const transformMeetings = (meetings) => {
  if (!Array.isArray(meetings)) return [];
  return meetings.map(transformMeeting);
};

/**
 * Transform a single club object
 * @param {Object} club - Raw club data from API
 * @returns {Object} Transformed club data
 */
export const transformClub = (club) => {
  if (!club) return null;
  
  return {
    id: cleanId(club.id || club._id),
    name: club.name,
    description: club.description,
    city: club.city,
    state: club.state,
    club_type: club.type,
    skill_level: club.skill_level,
    current_connections: club.current_connections || 0,
    members: club.members || 0,
    contact_email: club.contact_email,
    contact_phone: club.contact_phone,
    organizer_name: club.organizer_name,
    is_active: club.is_active !== false, // Default to true if not set
    is_public: club.is_public !== false, // Default to true if not set
    created_at: club.created_at,
    updated_at: club.updated_at,
    profile_picture: club.profile_picture,
    runcard_image: getPrimaryImageUrl(club.clubcard_images),
    runcard_images: club.clubcard_images, // Full images array
    clubcard_images: club.clubcard_images, // Alias for mobile parity
    // Additional fields for ClubCard compatibility
    type: club.type || 'Social',
    organizer: club.organizer_name, // Alias for ClubCard compatibility
    nextRun: club.next_run || null,
    // Rating fields
    rating: club.rating,
    total_ratings: club.total_ratings || 0,
    // Mobile parity: location and distance
    home_base_address: club.home_base_address || null,
    distance_from_user: club.distance_from_user,
    distanceFromUser: club.distance_from_user,
    is_anchor_club: club.is_anchor_club === true
  };
};

/**
 * Transform an array of clubs
 * @param {Array} clubs - Array of raw club data
 * @returns {Array} Array of transformed club data
 */
export const transformClubs = (clubs) => {
  if (!Array.isArray(clubs)) return [];
  return clubs.map(transformClub);
};

/**
 * Transform a single user object
 * @param {Object} user - Raw user data from API
 * @returns {Object} Transformed user data
 */
export const transformUser = (user) => {
  if (!user) return null;
  
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    phone: user.phone,
    user_type: user.user_type,
    location: formatProfileLocationLabel(user) || user.city,
    country: user.country,
    state: user.state,
    run_club_name: user.run_club_name,
    club_id: user.club_id,
    is_setup_complete: user.is_setup_complete,
    profile_picture: user.profile_picture,
    created_at: user.created_at,
    updated_at: user.updated_at
  };
};

/**
 * Transform an array of users
 * @param {Array} users - Array of raw user data
 * @returns {Array} Array of transformed user data
 */
export const transformUsers = (users) => {
  if (!Array.isArray(users)) return [];
  return users.map(transformUser);
};

/**
 * Transform a single registration object
 * @param {Object} registration - Raw registration data from API
 * @returns {Object} Transformed registration data
 */
export const transformRegistration = (registration) => {
  if (!registration) return null;
  
  return {
    id: registration.id,
    user_id: registration.user_id,
    club_id: registration.club_id,
    meeting_id: registration.meeting_id,
    status: registration.status,
    registered_at: registration.registered_at,
    meeting: registration.meeting ? transformMeeting(registration.meeting) : null,
    club: registration.club ? transformClub(registration.club) : null
  };
};

/**
 * Transform an array of registrations
 * @param {Array} registrations - Array of raw registration data
 * @returns {Array} Array of transformed registration data
 */
export const transformRegistrations = (registrations) => {
  if (!Array.isArray(registrations)) return [];
  return registrations.map(transformRegistration);
};

/**
 * Transform Strava activity data
 * @param {Object} activity - Raw Strava activity data
 * @returns {Object} Transformed activity data
 */
export const transformStravaActivity = (activity) => {
  if (!activity) return null;
  
  return {
    id: activity.id,
    name: activity.name,
    distance: activity.distance,
    moving_time: activity.moving_time,
    elapsed_time: activity.elapsed_time,
    total_elevation_gain: activity.total_elevation_gain,
    type: activity.type,
    start_date: activity.start_date,
    start_date_local: activity.start_date_local,
    timezone: activity.timezone,
    average_speed: activity.average_speed,
    max_speed: activity.max_speed,
    average_cadence: activity.average_cadence,
    average_heartrate: activity.average_heartrate,
    max_heartrate: activity.max_heartrate,
    calories: activity.calories,
    description: activity.description,
    external_id: activity.external_id
  };
};

/**
 * Transform an array of Strava activities
 * @param {Array} activities - Array of raw Strava activity data
 * @returns {Array} Array of transformed activity data
 */
export const transformStravaActivities = (activities) => {
  if (!Array.isArray(activities)) return [];
  return activities.map(transformStravaActivity);
};

/**
 * Format date for display (in meeting timezone if provided)
 * @param {string|Date} date - Date to format (UTC)
 * @param {Object} options - Formatting options
 * @param {string} options.timezone - IANA timezone string (optional)
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';
  
  const {
    includeTime = false,
    format = 'short',
    timezone = null
  } = options;
  
  // Convert UTC to meeting timezone if provided
  const dateObj = convertUTCToMeetingTimezone(date, timezone);
  if (!dateObj || isNaN(dateObj.getTime())) return '';
  
  const formatOptions = {
    short: { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    },
    long: { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    },
    time: { 
      hour: '2-digit', 
      minute: '2-digit' 
    }
  };
  
  const selectedFormat = formatOptions[format] || formatOptions.short;
  
  if (includeTime) {
    return dateObj.toLocaleDateString('en-US', {
      ...selectedFormat,
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone || undefined
    });
  }
  
  return dateObj.toLocaleDateString('en-US', {
    ...selectedFormat,
    timeZone: timezone || undefined
  });
};

/**
 * Calculate time until a future date
 * @param {string|Date} futureDate - Future date
 * @returns {string|null} Time until string or null if date is in the past
 */
export const getTimeUntil = (futureDate) => {
  if (!futureDate) return null;
  
  const now = new Date();
  const targetDate = new Date(futureDate);
  
  if (isNaN(targetDate.getTime()) || targetDate <= now) {
    return null;
  }
  
  // Get dates without time to compare just the day
  const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetDateOnly = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
  
  const timeDiff = targetDateOnly.getTime() - nowDate.getTime();
  const daysDiff = Math.round(timeDiff / (1000 * 3600 * 24));
  
  if (daysDiff === 0) {
    return "Today";
  } else if (daysDiff === 1) {
    return "Tomorrow";
  } else if (daysDiff <= 7) {
    return `In ${daysDiff} days`;
  } else if (daysDiff <= 30) {
    const weeks = Math.floor(daysDiff / 7);
    return `In ${weeks} week${weeks > 1 ? 's' : ''}`;
  } else {
    const months = Math.floor(daysDiff / 30);
    return `In ${months} month${months > 1 ? 's' : ''}`;
  }
};

/**
 * Format time for display (in meeting timezone if provided)
 * @param {string|Date} date - Date to extract time from (UTC)
 * @param {string} timezone - IANA timezone string (optional)
 * @returns {string} Formatted time string
 */
export const formatTime = (date, timezone = null) => {
  if (!date) return '';
  
  // Convert UTC to meeting timezone if provided
  const dateObj = convertUTCToMeetingTimezone(date, timezone);
  if (!dateObj || isNaN(dateObj.getTime())) return '';
  
  const hours = dateObj.getHours();
  const minutes = dateObj.getMinutes();
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/**
 * Filter and sort meetings by date
 * @param {Array} meetings - Array of meeting objects
 * @param {Object} options - Filtering and sorting options
 * @returns {Array} Filtered and sorted meetings
 */
export const filterAndSortMeetings = (meetings, options = {}) => {
  // Input validation
  if (!Array.isArray(meetings)) {
    // Meetings must be an array
    return [];
  }
  
  const {
    status = 'upcoming',
    limit = null,
    sortBy = 'scheduled_date',
    sortOrder = 'asc'
  } = options;
  
  let filtered = [...meetings];
  
  // Filter by status - only check database status field
  if (status === 'upcoming') {
    filtered = filtered.filter(meeting => {
      return meeting.status === 'upcoming';
    });
  } else if (status === 'past') {
    // For past meetings, check database status
    filtered = filtered.filter(meeting => {
      return meeting.status === 'completed' || meeting.status === 'cancelled';
    });
  } else if (status && status !== 'all') {
    filtered = filtered.filter(meeting => meeting.status === status);
  }
  
  // Sort with safety checks
  filtered.sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];
    
    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return sortOrder === 'asc' ? 1 : -1;
    if (bValue == null) return sortOrder === 'asc' ? -1 : 1;
    
    if (sortBy === 'scheduled_date') {
      const aDate = new Date(aValue);
      const bDate = new Date(bValue);
      
      // Handle invalid dates
      if (isNaN(aDate.getTime()) && isNaN(bDate.getTime())) return 0;
      if (isNaN(aDate.getTime())) return 1;
      if (isNaN(bDate.getTime())) return -1;
      
      return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
    }
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
  });
  
  // Apply limit
  if (limit && limit > 0) {
    filtered = filtered.slice(0, limit);
  }
  
  return filtered;
};

/**
 * Transform meeting data to MeetingCard format with consistent fields
 * @param {Object} meeting - Meeting object
 * @returns {Object} MeetingCard formatted object
 */
export const transformMeetingToRunCard = (meeting) => {
  try {
    // Validate meeting object
    if (!meeting || !meeting.scheduled_date) {
      throw new Error('Missing meeting data or scheduled_date');
    }
    
    // Convert UTC to meeting timezone for display
    const meetingTimezone = meeting.timezone || null;
    const scheduledDate = convertUTCToMeetingTimezone(meeting.scheduled_date, meetingTimezone);
    if (!scheduledDate) {
      throw new Error('Invalid date format');
    }
    
    if (isNaN(scheduledDate.getTime())) {
      throw new Error('Invalid date format');
    }
    
    const now = new Date();
    const timeDiff = scheduledDate.getTime() - now.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    let timeUntil = null;
    
    if (daysDiff === 0) {
      timeUntil = "Today";
    } else if (daysDiff === 1) {
      timeUntil = "Tomorrow";
    } else if (daysDiff <= 7) {
      timeUntil = `In ${daysDiff} days`;
    }
    
    const timeString = formatTimeInTimezone(meeting.scheduled_date, meetingTimezone);
    const dateWithDayLabel = formatDateWithTimeRelative(meeting.scheduled_date, meetingTimezone, { includeTime: false });

    const fallbackImage = "https://images.unsplash.com/photo-1598012268972-217e6036c419?w=400&h=300&fit=crop";
    return {
      id: meeting.id,
      club_id: meeting.club_id,
      scheduledDate: scheduledDate,
      type: meeting.event_type || "Social",
      title: meeting.name,
      name: meeting.name,
      description: meeting.what_to_expect,
      location: meeting.address,
      venue: meeting.address ?? "",
      city: [meeting.city, meeting.state].filter(Boolean).join(", "),
      cityOnly: meeting.city ?? "",
      stateOnly: meeting.state ?? "",
      time: timeString,
      dateWithDayLabel,
      timeUntil: timeUntil,
      attendees: meeting.current_participants || 0,
      image: getPrimaryImageUrl(meeting.images, fallbackImage),
      images: meeting.images ?? [],
      date: scheduledDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }).toUpperCase(),
      difficulty: meeting.difficulty ?? "",
      is_recurring: meeting.is_recurring || false,
      recurrence_pattern: meeting.recurrence_pattern || null,
      distance_from_user: meeting.distance_from_user,
      distanceFromUser: meeting.distance_from_user != null ? meeting.distance_from_user : undefined,
      club_name: meeting.club_name ?? undefined
    };
  } catch (error) {
    // Return a safe default for invalid meetings
    const fallbackImage = "https://images.unsplash.com/photo-1598012268972-217e6036c419?w=400&h=300&fit=crop";
    return {
      id: meeting.id,
      club_id: meeting.club_id,
      club_name: meeting.club_name ?? undefined,
      day: "UNKNOWN",
      type: meeting.event_type || "Social",
      title: meeting.name || "Untitled Event",
      name: meeting.name || "Untitled Event",
      description: meeting.what_to_expect,
      location: meeting.address || "Unknown Location",
      venue: meeting.address || "Location",
      city: `${meeting.city || "Unknown"}, ${meeting.state || "Unknown"}`,
      time: "Unknown Time",
      timeUntil: null,
      dateWithDayLabel: 'TBD',
      attendees: meeting.current_participants || 0,
      image: getPrimaryImageUrl(meeting.images, fallbackImage),
      images: meeting.images ?? [],
      date: "Unknown Date",
      difficulty: meeting.difficulty ?? "",
      is_recurring: meeting.is_recurring || false,
      recurrence_pattern: meeting.recurrence_pattern || null,
      distance_from_user: meeting.distance_from_user,
      distanceFromUser: meeting.distance_from_user != null ? meeting.distance_from_user : undefined
    };
  }
};
